import React, { useCallback, useEffect, useState } from "react";
import {
  approveAgencyOnboardingAdmin,
  getAgencyOnboardingsAdmin,
  getUsersGroupedByRole,
  rejectAgencyOnboardingAdmin,
  IAgencyOnboardingData,
  ApproveAgencyOnboardingPayload,
} from "../../services/usersapi";

type AgencyAdminManagerOption = {
  _id: string;
  username: string;
  email?: string;
  phone?: string;
  location?: string;
};

type StatusFilter = "PENDING" | "ALL" | "APPROVED" | "REJECTED";

type Row = IAgencyOnboardingData & {
  _id: string;
  userId?:
    | null
    | string
    | {
        email?: string;
        username?: string;
        phone?: string;
        role?: string;
        isOnboarded?: boolean;
      };
};

const AgencyOnboardingApprovals: React.FC = () => {
  const [status, setStatus] = useState<StatusFilter>("PENDING");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveRow, setApproveRow] = useState<Row | null>(null);
  const [approvePassword, setApprovePassword] = useState("");
  const [approveManagerId, setApproveManagerId] = useState("");
  const [approveUsername, setApproveUsername] = useState("");
  const [agencyAdminManagers, setAgencyAdminManagers] = useState<AgencyAdminManagerOption[]>([]);
  const [agencyAdminListLoading, setAgencyAdminListLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAgencyOnboardingsAdmin(status);
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setAgencyAdminListLoading(true);
      try {
        const response = await getUsersGroupedByRole();
        if (cancelled) return;
        if (
          response?.status === "success" &&
          Array.isArray(response.data?.AGENCY_ADMIN)
        ) {
          setAgencyAdminManagers(response.data.AGENCY_ADMIN as AgencyAdminManagerOption[]);
        } else {
          setAgencyAdminManagers([]);
        }
      } catch {
        if (!cancelled) setAgencyAdminManagers([]);
      } finally {
        if (!cancelled) setAgencyAdminListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rowNeedsPortalUser = (r: Row) => r.userId == null;

  const onApproveClick = (r: Row) => {
    if (rowNeedsPortalUser(r)) {
      setApproveRow(r);
      setApprovePassword("");
      setApproveManagerId("");
      setApproveUsername(r.agencyName?.slice(0, 80) || "");
      return;
    }
    void onApproveConfirm(r._id);
  };

  const onApproveConfirm = async (
    id: string,
    payload?: ApproveAgencyOnboardingPayload,
  ) => {
    setBusyId(id);
    try {
      await approveAgencyOnboardingAdmin(id, payload);
      setApproveRow(null);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const onApproveExternalSubmit = () => {
    if (!approveRow) return;
    const p: ApproveAgencyOnboardingPayload = {
      password: approvePassword,
      assignedManagerUserId: approveManagerId.trim(),
      ...(approveUsername.trim() ? { username: approveUsername.trim() } : {}),
    };
    if (p.password.length < 6 || !p.assignedManagerUserId) return;
    void onApproveConfirm(approveRow._id, p);
  };

  const onRejectConfirm = async () => {
    if (!rejectId) return;
    setBusyId(rejectId);
    try {
      await rejectAgencyOnboardingAdmin(rejectId, rejectReason);
      setRejectId(null);
      setRejectReason("");
      await load();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Agency onboarding approvals
          </h1>
          <p className="text-sm text-gray-600">
            Review applications submitted from the portal or external
            integrations.
          </p>
        </div>
        <select
          className="rounded border border-gray-300 px-3 py-2 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
        >
          <option value="PENDING">Pending</option>
          <option value="ALL">All</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">No records found.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Agency
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Email
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Source
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Status
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Updated
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const email =
                  r.userId &&
                  typeof r.userId === "object" &&
                  "email" in r.userId &&
                  r.userId.email
                    ? r.userId.email
                    : r.agencyEmailId;
                const st = r.approvalStatus || "—";
                return (
                  <tr key={r._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {r.agencyName}
                    </td>
                    <td className="px-4 py-3 text-gray-700">{email}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.submissionSource == "EXTERNAL_API"
                        ? "External"
                        : "PORTAL"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          st === "APPROVED"
                            ? "bg-green-100 text-green-800"
                            : st === "REJECTED"
                              ? "bg-red-100 text-red-800"
                              : st === "PENDING"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {st}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {r.updatedDateTime?.slice(0, 16) || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(r.approvalStatus === "PENDING" ||
                        !r.approvalStatus) && (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={busyId === r._id}
                            className="rounded bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                            onClick={() => onApproveClick(r)}
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            disabled={busyId === r._id}
                            className="rounded bg-white px-3 py-1.5 text-xs font-medium text-red-700 ring-1 ring-red-200 hover:bg-red-50 disabled:opacity-50"
                            onClick={() => {
                              setRejectId(r._id);
                              setRejectReason("");
                            }}
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {approveRow ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-semibold text-gray-900">
              Create agency portal user
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              This application has no linked portal account yet. Set an initial
              password and choose the Agency Admin who will manage this agency.
            </p>
            <label className="mt-4 block text-xs font-medium text-gray-600">
              Username (optional)
            </label>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={approveUsername}
              onChange={(e) => setApproveUsername(e.target.value)}
              placeholder="Defaults from agency name"
            />
            <label className="mt-3 block text-xs font-medium text-gray-600">
              Password (min 6 characters)
            </label>
            <input
              type="password"
              className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm"
              value={approvePassword}
              onChange={(e) => setApprovePassword(e.target.value)}
              autoComplete="new-password"
            />
            <label className="mt-3 block text-xs font-medium text-gray-600">
              Assign to Agency Admin
            </label>
            {agencyAdminListLoading ? (
              <p className="mt-2 text-sm text-gray-500">
                Loading Agency Admin accounts…
              </p>
            ) : (
              <select
                className="mt-1 w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm"
                value={approveManagerId}
                onChange={(e) => setApproveManagerId(e.target.value)}
              >
                <option value="">Select an agency admin</option>
                {agencyAdminManagers.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.username}
                    {m.email ? ` — ${m.email}` : ""}
                  </option>
                ))}
              </select>
            )}
            {!agencyAdminListLoading && agencyAdminManagers.length === 0 ? (
              <p className="mt-1 text-xs text-amber-700">
                No Agency Admin users found. Create an Agency Admin before
                approving external agencies.
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setApproveRow(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !!busyId ||
                  agencyAdminListLoading ||
                  agencyAdminManagers.length === 0 ||
                  approvePassword.length < 6 ||
                  !approveManagerId.trim()
                }
                className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
                onClick={() => void onApproveExternalSubmit()}
              >
                Approve and create user
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {rejectId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-lg">
            <h2 className="text-lg font-semibold text-gray-900">
              Reject application
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              Optional note for the agency (stored on record).
            </p>
            <textarea
              className="mt-3 w-full rounded border border-gray-300 p-2 text-sm"
              rows={3}
              placeholder="Reason (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setRejectId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!!busyId}
                className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                onClick={() => void onRejectConfirm()}
              >
                Confirm reject
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AgencyOnboardingApprovals;
